import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.addEventListener(ThemeEvents.variantSelected, this.updateSelectedPrice);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.removeEventListener(ThemeEvents.variantSelected, this.updateSelectedPrice);
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newPrice = event.detail.data.html.querySelector('product-price [ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (!newPrice || !currentPrice) return;

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      currentPrice.replaceWith(newPrice);
    }
  };

  /**
   * Updates the visible price immediately for single-option PDP variant changes.
   * The later variant:update event still reconciles the server-rendered markup.
   * @param {CustomEvent} event
   */
  updateSelectedPrice = (event) => {
    if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const variant = event.detail?.resource;
    if (!variant?.price) return;

    const currentPrice = this.querySelector('[ref="priceContainer"]');
    if (!currentPrice) return;

    const price = currentPrice.querySelector('.price');
    if (price) {
      price.textContent = variant.price;
    }

    let compareAtPrice = currentPrice.querySelector('.compare-at-price');
    let compareAtPriceGroup = compareAtPrice?.closest('[role="group"]');

    if (!compareAtPrice && variant.compareAtPrice) {
      compareAtPriceGroup = document.createElement('span');
      compareAtPriceGroup.setAttribute('role', 'group');
      compareAtPriceGroup.innerHTML =
        '<span class="visually-hidden">Regular price&nbsp;</span><span class="compare-at-price"></span>';
      currentPrice.appendChild(compareAtPriceGroup);
      compareAtPrice = compareAtPriceGroup.querySelector('.compare-at-price');
    }

    if (!compareAtPriceGroup || !compareAtPrice) return;

    if (variant.showComparePrice && variant.compareAtPrice) {
      compareAtPrice.textContent = variant.compareAtPrice;
      compareAtPriceGroup.hidden = false;
    } else {
      compareAtPriceGroup.hidden = true;
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
